import { HIGHLIGHTS, HERO_IMAGES } from "../mock";
import { ArrowUpRight, TrendingUp, Sparkles, Star, Code2 } from "lucide-react";

function Card({ h, className = "", children, testId }) {
  const dark = h.color.includes("neutral-950");
  return (
    <div
      data-testid={testId}
      data-anim="up"
      className={`${h.color} ${className} relative rounded-[28px] p-7 md:p-8 overflow-hidden min-h-[300px] flex flex-col justify-between group`}
    >
      <img
        src={h.image}
        alt=""
        loading="lazy"
        className={`absolute inset-0 w-full h-full object-cover grayscale group-hover:scale-105 transition-all duration-700 pointer-events-none ${
          dark
            ? "opacity-20 mix-blend-screen group-hover:opacity-30"
            : "opacity-[0.14] mix-blend-multiply group-hover:opacity-25"
        }`}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/5 via-transparent to-transparent pointer-events-none" />
      <div className="relative z-10 flex flex-col justify-between h-full flex-1">{children}</div>
    </div>
  );
}

export default function Highlights() {
  const [chart, tools, clients] = HIGHLIGHTS;
  return (
    <section className="py-10 md:py-14 bg-white" data-testid="highlights-section">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-6 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <Card h={chart} testId="highlight-chart">
            <div>
              <span className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-white/80 text-xs font-semibold">
                <TrendingUp size={14} className="text-[#0A5C36]" /> Live metric
              </span>
              <div className="mt-6 flex items-baseline gap-2">
                <p className="display text-[56px] md:text-[64px] leading-none">{chart.metric}</p>
                <span className="text-xs text-black/60">this quarter</span>
              </div>
              <p className="text-sm text-black/70 mt-1">{chart.metricLabel}</p>
            </div>
            <div>
              <svg viewBox="0 0 300 80" className="w-full h-16" preserveAspectRatio="none">
                <path d="M0 60 L40 55 L70 45 L100 50 L140 30 L180 35 L220 20 L260 25 L300 5" stroke="#0A5C36" strokeWidth="3" fill="none" />
                <path d="M0 60 L40 55 L70 45 L100 50 L140 30 L180 35 L220 20 L260 25 L300 5 L300 80 L0 80 Z" fill="rgba(10,92,54,0.15)" />
                <circle cx="300" cy="5" r="6" fill="#0A5C36" className="animate-pulse" />
              </svg>
              <p className="display text-lg md:text-xl leading-tight mt-3">{chart.title}</p>
            </div>
          </Card>

          <Card h={tools} className="md:-translate-y-6" testId="highlight-tools">
            <div className="flex items-start justify-between">
              <p className="display text-2xl md:text-[28px] leading-tight max-w-[220px]">{tools.title}</p>
              <span className="w-10 h-10 rounded-full bg-[#FFE14D] text-black flex items-center justify-center shrink-0">
                <Code2 size={18} />
              </span>
            </div>
            <div className="flex flex-wrap gap-2 mt-6">
              {tools.tools.map((t, i) => (
                <span
                  key={t}
                  style={{ transform: `rotate(${i % 2 ? 1.5 : -1.5}deg)` }}
                  className="h-10 px-4 rounded-full border border-white/20 bg-white/[0.06] backdrop-blur flex items-center gap-2 text-sm font-medium hover:bg-[#FFE14D] hover:text-black hover:border-[#FFE14D] transition-colors"
                >
                  <Star size={10} className="fill-current" /> {t}
                </span>
              ))}
            </div>
          </Card>

          <Card h={clients} testId="highlight-clients">
            <div>
              <span className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-black text-white text-xs font-semibold">
                <Sparkles size={14} /> Trusted worldwide
              </span>
              <p className="display text-2xl md:text-[28px] leading-tight mt-6">{clients.title}</p>
              <div className="flex items-center gap-1 mt-3">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={14} className="text-black fill-black" />
                ))}
                <span className="text-xs font-semibold ml-1">5.0 on every project</span>
              </div>
            </div>
            <div className="flex items-center justify-between mt-6 gap-3">
              <div className="flex -space-x-3">
                {HERO_IMAGES.avatars.map((src, i) => (
                  <img key={i} src={src} alt="client" loading="lazy" className="w-10 h-10 rounded-full border-2 border-[#FFE14D] object-cover" />
                ))}
                <span className="relative z-10 w-10 h-10 rounded-full border-2 border-[#FFE14D] bg-black text-white text-[11px] font-bold flex items-center justify-center">
                  +45
                </span>
              </div>
              <a
                href="#contact"
                data-testid="highlight-start-now"
                className="h-10 pl-4 pr-1 rounded-full bg-black text-white text-sm font-semibold flex items-center gap-2 group/btn"
              >
                {clients.ctaLabel}
                <span className="w-8 h-8 rounded-full bg-[#FFE14D] text-black flex items-center justify-center group-hover/btn:rotate-45 transition-transform">
                  <ArrowUpRight size={14} />
                </span>
              </a>
            </div>
          </Card>
        </div>
      </div>
    </section>
  );
}
